<script setup lang="ts">
/**
 * ENG-436 — the submit control, plus a way to overrule it.
 *
 * A split control: the caller's own button stays the primary action (submit and
 * let the analyser decide), and the caret beside it opens the same two choices
 * the modifier keys offer. That matters most on a phone, where ⌘↵ and ⌥↵ can't
 * be pressed at all and this menu is the only way to force a mode.
 *
 * The primary control is passed in rather than rendered here, because its two
 * hosts look nothing alike — a solid "Go" button on the search screen, a round
 * send icon inside the chat pill. Only the caret and the menu are shared.
 */
import type { QueryMode } from '@/utils/query-routing';
import { Icon } from '@iconify/vue';
import { computed } from 'vue';
import { UiDropdownMenu } from '@/components/ui';
import { isTouchPrimary, modifierKeyLabels } from '@/utils/query-routing';

withDefaults(defineProps<{
   /** Greys out the caret alongside a disabled primary control. */
   disabled?: boolean;
   /**
    * Whether this control routes at all. False on the chat surfaces that only
    * ever talk to Luma (document, project, in-thread), where the primary
    * control passes through untouched.
    */
   showMenu?: boolean;
}>(), {
   disabled: false,
   showMenu: true,
});

const emit = defineEmits<{
   /** `null` means "analyse it for me" — what the primary control does. */
   force: [mode: QueryMode | null];
}>();

/**
 * The default is listed first so the menu states what the button already does,
 * which is the only place that behaviour is spelled out on a touch device.
 */
const items = computed(() => {
   const keys = modifierKeyLabels();
   const shortcut = (mode: QueryMode) => (isTouchPrimary() ? '' : `  ${keys[mode]}`);

   return [
      { id: 'auto', label: 'Decide for me', icon: 'ph:sparkle', divider: true },
      { id: 'search', label: `Search documents${shortcut('search')}`, icon: 'ph:magnifying-glass' },
      { id: 'chat', label: `Ask Luma${shortcut('chat')}`, icon: 'ph:chat-teardrop-text' },
   ];
});

function handleSelect(item: { id: string }) {
   emit('force', item.id === 'auto' ? null : (item.id as QueryMode));
}
</script>

<template>
   <div class="inline-flex items-center gap-1">
      <slot />

      <UiDropdownMenu
         v-if="showMenu"
         :items="items"
         placement="top-end"
         min-width="200px"
         @select="handleSelect"
      >
         <template #default="{ isOpen }">
            <button
               type="button"
               class="query-mode-caret"
               :class="{ 'query-mode-caret--open': isOpen }"
               :disabled="disabled"
               aria-label="Choose search or Luma"
               aria-haspopup="menu"
               :aria-expanded="isOpen"
            >
               <Icon
                  icon="ph:caret-down"
                  class="size-4 transition-transform"
                  :class="{ 'rotate-180': isOpen }"
               />
            </button>
         </template>
      </UiDropdownMenu>
   </div>
</template>

<style scoped lang="postcss">
.query-mode-caret {
   @apply flex items-center justify-center size-8 rounded-full shrink-0;
   @apply text-black/55 hover:bg-black/10 hover:text-black transition-colors;
   @apply disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent;
}

.query-mode-caret--open {
   @apply bg-black/10 text-black;
}
</style>
